import {
  SitePage,
  PageSection,
  HeaderMenuItem,
  IndustryType,
  SiteSettings,
  ComponentStyle,
} from '../types';

export interface SiteStructureOptions {
  siteId: string;
  siteName: string;
  slug: string;
  industry: IndustryType;
  contactInfo?: {
    phone?: string;
    email?: string;
    address?: string;
    whatsapp?: string;
  };
  colorPalette?: {
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    backgroundColor?: string;
    textColor?: string;
  };
  typography?: {
    fontFamily?: string;
    headingFont?: string;
  };
  userPrompt?: string;
}

export interface GeneratedSiteStructure {
  pages: SitePage[];
  headerMenu: HeaderMenuItem[];
  defaultSettings: Partial<SiteSettings>;
}

export class SiteStructureService {
  private static generateUniqueId(prefix: string): string {
    return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 7)}`;
  }

  /**
   * Builds the complete, mandatory structure for a website based on its industry sector.
   * Content policy: Structure is 100% complete and usable, content contains zero fake demo data.
   */
  public static buildStructure(options: SiteStructureOptions): GeneratedSiteStructure {
    const {
      siteId,
      siteName,
      slug,
      industry,
      contactInfo = {},
      colorPalette = {},
      typography = {},
      userPrompt = '',
    } = options;

    const isYacht = industry === 'YACHT_RENTAL';

    const primaryColor = colorPalette.primaryColor || (isYacht ? '#0369A1' : '#1E40AF');
    const secondaryColor = colorPalette.secondaryColor || (isYacht ? '#0C4A6E' : '#0F172A');
    const accentColor = colorPalette.accentColor || (isYacht ? '#F59E0B' : '#2563EB');
    const fontFamily = typography.fontFamily || 'Inter, system-ui, sans-serif';
    const headingFont = typography.headingFont || 'Plus Jakarta Sans, sans-serif';

    const now = Date.now();

    // Default Header Menu Items
    let headerMenu: HeaderMenuItem[] = [];
    let pages: SitePage[] = [];

    if (isYacht) {
      // 7 Mandatory Pages for Yacht Rental
      headerMenu = [
        {
          id: this.generateUniqueId('menu'),
          label: 'Ana Sayfa',
          targetType: 'page',
          type: 'page',
          targetId: '/',
          target: '/',
          order: 0,
        },
        {
          id: this.generateUniqueId('menu'),
          label: 'Yatlarımız',
          targetType: 'page',
          type: 'page',
          targetId: '/yatlar',
          target: '/yatlar',
          order: 1,
        },
        {
          id: this.generateUniqueId('menu'),
          label: 'Rotalar',
          targetType: 'page',
          type: 'page',
          targetId: '/rotalar',
          target: '/rotalar',
          order: 2,
        },
        {
          id: this.generateUniqueId('menu'),
          label: 'Hakkımızda',
          targetType: 'page',
          type: 'page',
          targetId: '/hakkimizda',
          target: '/hakkimizda',
          order: 3,
        },
        {
          id: this.generateUniqueId('menu'),
          label: 'Galeri',
          targetType: 'page',
          type: 'page',
          targetId: '/galeri',
          target: '/galeri',
          order: 4,
        },
        {
          id: this.generateUniqueId('menu'),
          label: 'Rezervasyon',
          targetType: 'page',
          type: 'page',
          targetId: '/rezervasyon',
          target: '/rezervasyon',
          order: 5,
        },
        {
          id: this.generateUniqueId('menu'),
          label: 'İletişim',
          targetType: 'page',
          type: 'page',
          targetId: '/iletisim',
          target: '/iletisim',
          order: 6,
        },
      ];

      // Page 1: Ana Sayfa
      pages.push({
        id: this.generateUniqueId('page_home'),
        siteId,
        title: 'Ana Sayfa',
        slug: '/',
        seoTitle: `${siteName} — Lüks Mavi Tur & Yat Kiralama`,
        seoDescription: `${siteName} ile Göcek, Bodrum, Fethiye ve Marmaris koylarında unutulmaz lüks mavi tur deneyimi.`,
        isHomePage: true,
        isPublished: true,
        order: 0,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Rezervasyon Yap', '/rezervasyon', true),
          {
            id: this.generateUniqueId('sec_hero'),
            siteId,
            type: 'HERO',
            name: 'Hero Karşılama Alanı',
            isVisible: true,
            order: 1,
            content: {
              badge: 'LÜKS MAVİ TUR & YAT KİRALAMA',
              title: `${siteName} ile Masmavi Koylarda Özgürlük`,
              subtitle:
                userPrompt ||
                'Ege ve Akdeniz’in en seçkin koylarında size ve sevdiklerinize özel, konfor dolu bir mavi yolculuk deneyimi.',
              description:
                'Göcek, Fethiye, Bodrum ve Marmaris çıkışlı lüks gulet, motor yat ve katamaran seçeneklerimizle unutulmaz anlar yaşayın.',
              buttonText: 'Filomuzu İnceleyin',
              buttonLink: '/yatlar',
              secondaryButtonText: 'Rotaları Keşfedin',
              secondaryButtonLink: '/rotalar',
              bgImageUrl:
                'https://images.unsplash.com/photo-1569263979104-865ab7cd8d13?auto=format&fit=crop&w=1920&q=85',
              bgOverlayOpacity: 65,
            },
            style: { textColor: '#FFFFFF', paddingTop: '110px', paddingBottom: '110px' },
            createdAt: now,
            updatedAt: now,
          },
          {
            id: this.generateUniqueId('sec_fleet'),
            siteId,
            type: 'YACHT_FLEET',
            name: 'Öne Çıkan Yatlar',
            isVisible: true,
            order: 2,
            content: {
              badge: 'SEÇKİN FİLO',
              title: 'Öne Çıkan Yatlarımız',
              description:
                'Konfor, estetik ve güvenliği bir arada sunan özel yat ve teknelerimiz ile denize açılın.',
              items: [],
            },
            style: { backgroundColor: '#0B132B', textColor: '#FFFFFF' },
            createdAt: now,
            updatedAt: now,
          },
          {
            id: this.generateUniqueId('sec_routes'),
            siteId,
            type: 'YACHT_ROUTES',
            name: 'Popüler Rotalar',
            isVisible: true,
            order: 3,
            content: {
              badge: 'DESTİNASYONLAR',
              title: 'Popüler Mavi Tur Rotaları',
              description:
                'Doğal güzellikleri, kristal berraklığındaki suları ve tarihi koyları keşfedin.',
              items: [],
            },
            style: { backgroundColor: '#0F172A', textColor: '#FFFFFF' },
            createdAt: now,
            updatedAt: now,
          },
          {
            id: this.generateUniqueId('sec_stats'),
            siteId,
            type: 'STATS',
            name: 'Ayrıcalıklar & Neden Biz',
            isVisible: true,
            order: 4,
            content: {
              badge: 'NEDEN BİZ',
              title: 'Kusursuz Denizcilik Standartları',
              items: [],
            },
            style: { backgroundColor: '#090E1A', textColor: '#FFFFFF' },
            createdAt: now,
            updatedAt: now,
          },
          {
            id: this.generateUniqueId('sec_res'),
            siteId,
            type: 'RESERVATION_CTA',
            name: 'Rezervasyon Ön Talep',
            isVisible: true,
            order: 5,
            content: {
              badge: 'REZERVASYON',
              title: 'Mavi Turunuzu Hemen Planlayın',
              description:
                'Tarih ve tercihlerinizi iletin, uzman yat danışmanlarımız size özel teklif hazırlasın.',
              buttonText: 'Rezervasyon Talebi Oluştur',
              buttonLink: '/rezervasyon',
            },
            style: { backgroundColor: '#0F172A', textColor: '#FFFFFF' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, true),
        ],
      });

      // Page 2: Yatlarımız (Filo)
      pages.push({
        id: this.generateUniqueId('page_yachts'),
        siteId,
        title: 'Yatlarımız',
        slug: '/yatlar',
        seoTitle: `Filomuz — ${siteName}`,
        seoDescription: `${siteName} kiralık motor yat, katamaran ve lüks gulet filosu.`,
        isHomePage: false,
        isPublished: true,
        order: 1,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Rezervasyon Yap', '/rezervasyon', true),
          this.createPageHeroSection(
            siteId,
            'SEÇKİN FİLO',
            'Lüks Yat ve Gulet Filomuz',
            'Tüm teknik donanımları tamamlanmış, yüksek konfor standartlarına sahip kiralık teknelerimiz.',
            'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=1920&q=80',
            true
          ),
          {
            id: this.generateUniqueId('sec_yacht_list'),
            siteId,
            type: 'YACHT_FLEET',
            name: 'Yat Listesi',
            isVisible: true,
            order: 2,
            content: {
              badge: 'KİRALIK TEKNELER',
              title: 'Tüm Kiralık Yatlarımız',
              description: 'Kabin sayısı, kapasite ve özelliklerine göre inceleyin.',
            },
            style: { backgroundColor: '#0B132B', textColor: '#FFFFFF' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, true),
        ],
      });

      // Page 3: Rotalar
      pages.push({
        id: this.generateUniqueId('page_routes'),
        siteId,
        title: 'Rotalar',
        slug: '/rotalar',
        seoTitle: `Mavi Tur Rotaları — ${siteName}`,
        seoDescription: `Göcek, Bodrum, Marmaris ve Fethiye mavi tur seyir rotaları.`,
        isHomePage: false,
        isPublished: true,
        order: 2,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Rezervasyon Yap', '/rezervasyon', true),
          this.createPageHeroSection(
            siteId,
            'DESTİNASYONLAR',
            'Ege ve Akdeniz Mavi Tur Rotaları',
            'Göcek adalarından Hisarönü körfezine, Bodrum koylarından Datça’ya uzanan eşsiz rotalar.',
            'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1920&q=80',
            true
          ),
          {
            id: this.generateUniqueId('sec_route_cards'),
            siteId,
            type: 'YACHT_ROUTES',
            name: 'Rota Kartları',
            isVisible: true,
            order: 2,
            content: {
              badge: 'ÖNERİLEN PROGRAMLAR',
              title: 'Haftalık Seyir Rotaları',
              description: 'Kaptanlarımızın tavsiyeleriyle oluşturulmuş popüler rotalar.',
            },
            style: { backgroundColor: '#0F172A', textColor: '#FFFFFF' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, true),
        ],
      });

      // Page 4: Hakkımızda
      pages.push({
        id: this.generateUniqueId('page_about'),
        siteId,
        title: 'Hakkımızda',
        slug: '/hakkimizda',
        seoTitle: `Hakkımızda — ${siteName}`,
        seoDescription: `${siteName} denizcilik tutkusu ve lüks mavi tur hizmet anlayışı.`,
        isHomePage: false,
        isPublished: true,
        order: 3,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Rezervasyon Yap', '/rezervasyon', true),
          this.createPageHeroSection(
            siteId,
            'KURUMSAL',
            'Deniz Tutkusu ve Ayrıcalıklı Hizmet',
            'Denizde geçen her anın kusursuz ve unutulmaz olması için özenle çalışıyoruz.',
            'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1920&q=80',
            true
          ),
          {
            id: this.generateUniqueId('sec_about_story'),
            siteId,
            type: 'ABOUT',
            name: 'Kurumsal Hikaye',
            isVisible: true,
            order: 2,
            content: {
              badge: 'HİKAYEMİZ',
              title: 'Masmavi Bir Dünyada Güven ve Konfor',
              description: `${siteName}, deniz sevgisini yüksek standartlarda hizmet kalitesiyle buluşturmak üzere kurulmuştur. Deneyimli kaptanlarımız, profesyonel aşçılarımız ve güler yüzlü mürettebatımızla her ayrıntıyı sizin için düşünüyoruz.`,
              imageUrl:
                'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=1200&q=80',
              buttonText: 'Rezervasyon Oluştur',
              buttonLink: '/rezervasyon',
            },
            style: { backgroundColor: '#0B132B', textColor: '#FFFFFF' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, true),
        ],
      });

      // Page 5: Galeri
      pages.push({
        id: this.generateUniqueId('page_gallery'),
        siteId,
        title: 'Galeri',
        slug: '/galeri',
        seoTitle: `Fotoğraf Galerisi — ${siteName}`,
        seoDescription: `Mavi tur, teknelerimiz ve koylarımızdan seçkin fotoğraf galerisi.`,
        isHomePage: false,
        isPublished: true,
        order: 4,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Rezervasyon Yap', '/rezervasyon', true),
          this.createPageHeroSection(
            siteId,
            'FOTOĞRAFLAR',
            'Mavi Yolculuktan Seçkin Kareler',
            'Koylarımız, gün batımları ve güvertedeki keyif dolu anlar.',
            'https://images.unsplash.com/photo-1510414842594-a61c69b5ae57?auto=format&fit=crop&w=1920&q=80',
            true
          ),
          {
            id: this.generateUniqueId('sec_gallery_grid'),
            siteId,
            type: 'GALLERY',
            name: 'Medya Izgarası',
            isVisible: true,
            order: 2,
            content: {
              badge: 'GÖRSEL ARŞİV',
              title: 'Yatlarımız ve Koylar',
              description: 'Ege ve Akdeniz seyirlerimizden büyüleyici görüntüler.',
            },
            style: { backgroundColor: '#0B132B', textColor: '#FFFFFF' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, true),
        ],
      });

      // Page 6: Rezervasyon
      pages.push({
        id: this.generateUniqueId('page_booking'),
        siteId,
        title: 'Rezervasyon',
        slug: '/rezervasyon',
        seoTitle: `Rezervasyon Talebi — ${siteName}`,
        seoDescription: `Mavi tur yat kiralama ön rezervasyon formu.`,
        isHomePage: false,
        isPublished: true,
        order: 5,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Rezervasyon Yap', '/rezervasyon', true),
          this.createPageHeroSection(
            siteId,
            'ONLINE REZERVASYON',
            'Rezervasyon Talebi Oluşturun',
            'Tarih, rota ve kişi sayınızı iletin; size en uygun tekne teklifini hazırlayalım.',
            'https://images.unsplash.com/photo-1569263979104-865ab7cd8d13?auto=format&fit=crop&w=1920&q=85',
            true
          ),
          {
            id: this.generateUniqueId('sec_booking_form'),
            siteId,
            type: 'RESERVATION_CTA',
            name: 'Rezervasyon Formu',
            isVisible: true,
            order: 2,
            content: {
              badge: 'TALEP FORMU',
              title: 'Yat Kiralama Talep Formu',
              description:
                'Aşağıdaki formu doldurduktan sonra yetkili danışmanımız en kısa sürede sizinle iletişime geçecektir.',
              buttonText: 'Talebi İlet',
            },
            style: { backgroundColor: '#0B132B', textColor: '#FFFFFF' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, true),
        ],
      });

      // Page 7: İletişim
      pages.push({
        id: this.generateUniqueId('page_contact'),
        siteId,
        title: 'İletişim',
        slug: '/iletisim',
        seoTitle: `İletişim & Konum — ${siteName}`,
        seoDescription: `${siteName} iletişim bilgileri, adres, telefon ve harita konumu.`,
        isHomePage: false,
        isPublished: true,
        order: 6,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Rezervasyon Yap', '/rezervasyon', true),
          this.createPageHeroSection(
            siteId,
            'İLETİŞİM',
            'Bizimle İletişime Geçin',
            'Yat kiralama danışmanlarımız rota ve tekne seçimi için 7/24 hizmetinizdedir.',
            'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1920&q=80',
            true
          ),
          {
            id: this.generateUniqueId('sec_contact_form'),
            siteId,
            type: 'CONTACT',
            name: 'İletişim & Konum',
            isVisible: true,
            order: 2,
            content: {
              badge: 'BİZE ULAŞIN',
              title: 'Kesintisiz İletişim ve Destek',
              description:
                'Görüş, soru ve kiralama talepleriniz için aşağıdaki formu doldurabilir veya WhatsApp üzerinden doğrudan bize yazabilirsiniz.',
              buttonText: 'Mesaj Gönder',
              phone: contactInfo.phone || '',
              email: contactInfo.email || '',
              address: contactInfo.address || '',
            },
            style: { backgroundColor: '#0B132B', textColor: '#FFFFFF' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, true),
        ],
      });
    } else {
      // 6 Mandatory Pages for Corporate Website
      headerMenu = [
        {
          id: this.generateUniqueId('menu'),
          label: 'Ana Sayfa',
          targetType: 'page',
          type: 'page',
          targetId: '/',
          target: '/',
          order: 0,
        },
        {
          id: this.generateUniqueId('menu'),
          label: 'Hakkımızda',
          targetType: 'page',
          type: 'page',
          targetId: '/hakkimizda',
          target: '/hakkimizda',
          order: 1,
        },
        {
          id: this.generateUniqueId('menu'),
          label: 'Hizmetlerimiz',
          targetType: 'page',
          type: 'page',
          targetId: '/hizmetler',
          target: '/hizmetler',
          order: 2,
        },
        {
          id: this.generateUniqueId('menu'),
          label: 'Projelerimiz',
          targetType: 'page',
          type: 'page',
          targetId: '/projeler',
          target: '/projeler',
          order: 3,
        },
        {
          id: this.generateUniqueId('menu'),
          label: 'Galeri',
          targetType: 'page',
          type: 'page',
          targetId: '/galeri',
          target: '/galeri',
          order: 4,
        },
        {
          id: this.generateUniqueId('menu'),
          label: 'İletişim',
          targetType: 'page',
          type: 'page',
          targetId: '/iletisim',
          target: '/iletisim',
          order: 5,
        },
      ];

      // Page 1: Ana Sayfa
      pages.push({
        id: this.generateUniqueId('page_home'),
        siteId,
        title: 'Ana Sayfa',
        slug: '/',
        seoTitle: `${siteName} — Resmi Kurumsal Web Sitesi`,
        seoDescription: `${siteName} kurumsal çözümler, uzman kadro ve profesyonel hizmetler.`,
        isHomePage: true,
        isPublished: true,
        order: 0,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Teklif Alın', '/iletisim', false),
          {
            id: this.generateUniqueId('sec_hero'),
            siteId,
            type: 'HERO',
            name: 'Hero Karşılama Alanı',
            isVisible: true,
            order: 1,
            content: {
              badge: 'KURUMSAL ÇÖZÜMLER & DANIŞMANLIK',
              title: `${siteName} ile Geleceğe Güvenle İlerleyin`,
              subtitle:
                userPrompt ||
                'Yüksek kalite standartları, müşteri memnuniyeti ve sürdürülebilir başarı odaklı profesyonel çözümler.',
              description:
                'Sektörel bilgi birikimimiz ve çok disiplinli uzman kadromuz ile kurumunuzun büyüme hedeflerine güç katıyoruz.',
              buttonText: 'Hizmetlerimizi İnceleyin',
              buttonLink: '/hizmetler',
              secondaryButtonText: 'Kurumsal Hikayemiz',
              secondaryButtonLink: '/hakkimizda',
              bgImageUrl:
                'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1600&auto=format&fit=crop',
              bgOverlayOpacity: 75,
            },
            style: { textColor: '#FFFFFF', paddingTop: '100px', paddingBottom: '100px' },
            createdAt: now,
            updatedAt: now,
          },
          {
            id: this.generateUniqueId('sec_about_summary'),
            siteId,
            type: 'ABOUT',
            name: 'Hakkımızda Özeti',
            isVisible: true,
            order: 2,
            content: {
              badge: 'BİZ KİMİZ',
              title: 'Yenilikçi Strateji, Disiplinli Uygulama ve Ölçülebilir Sonuçlar',
              description: `${siteName} olarak pazar dinamiklerini derinlemesine analiz ediyor, kurumların karmaşık zorluklarını stratejik avantaja dönüştürüyoruz. Sektörel birikimimizle yanınızdayız.`,
              imageUrl:
                'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1200&auto=format&fit=crop',
              buttonText: 'Detaylı Bilgi',
              buttonLink: '/hakkimizda',
            },
            style: { backgroundColor: '#FFFFFF', textColor: '#0F172A' },
            createdAt: now,
            updatedAt: now,
          },
          {
            id: this.generateUniqueId('sec_services_grid'),
            siteId,
            type: 'SERVICES',
            name: 'Öne Çıkan Hizmetler',
            isVisible: true,
            order: 3,
            content: {
              badge: 'UZMANLIK ALANLARIMIZ',
              title: 'İşletmenizi Geleceğe Taşıyan Profesyonel Çözümler',
              description:
                'Modern iş dünyasının ihtiyaç duyduğu tüm kurumsal operasyonları tek çatı altında sunuyoruz.',
              items: [],
            },
            style: { backgroundColor: '#F8FAFC', textColor: '#0F172A' },
            createdAt: now,
            updatedAt: now,
          },
          {
            id: this.generateUniqueId('sec_stats'),
            siteId,
            type: 'STATS',
            name: 'Neden Biz / İstatistikler',
            isVisible: true,
            order: 4,
            content: {
              badge: 'NEDEN BİZ',
              title: 'Rakamlarla Başarı Göstergelerimiz',
              items: [],
            },
            style: { backgroundColor: '#FFFFFF', textColor: '#0F172A' },
            createdAt: now,
            updatedAt: now,
          },
          {
            id: this.generateUniqueId('sec_contact_cta'),
            siteId,
            type: 'CONTACT',
            name: 'İletişim & Teklif Formu',
            isVisible: true,
            order: 5,
            content: {
              badge: 'BİZE ULAŞIN',
              title: 'Projeleriniz İçin Hemen İletişime Geçin',
              description:
                'Kurumsal ihtiyaçlarınız ve projeleriniz için uzman danışmanlarımızla hemen irtibata geçin.',
              buttonText: 'Mesaj Gönder',
              phone: contactInfo.phone || '',
              email: contactInfo.email || '',
              address: contactInfo.address || '',
            },
            style: { backgroundColor: '#0F172A', textColor: '#FFFFFF' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, false),
        ],
      });

      // Page 2: Hakkımızda
      pages.push({
        id: this.generateUniqueId('page_about'),
        siteId,
        title: 'Hakkımızda',
        slug: '/hakkimizda',
        seoTitle: `Hakkımızda — ${siteName}`,
        seoDescription: `${siteName} kurumsal tarihçe, misyon, vizyon ve değerler.`,
        isHomePage: false,
        isPublished: true,
        order: 1,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Teklif Alın', '/iletisim', false),
          this.createPageHeroSection(
            siteId,
            'KURUMSAL KİMLİK',
            'Geleceği Birlikte İnşa Ediyoruz',
            'Köklü tecrübemiz ve ilkeli duruşumuzla sektörde güven inşa ediyoruz.',
            'https://images.unsplash.com/photo-1497215728101-856f4ea42174?q=80&w=1600&auto=format&fit=crop',
            false
          ),
          {
            id: this.generateUniqueId('sec_about_mv'),
            siteId,
            type: 'ABOUT',
            name: 'Misyon ve Vizyon',
            isVisible: true,
            order: 2,
            content: {
              badge: 'MİSYON & VİZYON',
              title: 'Değer Üreten, Güven İnşa Eden Deneyim',
              description: `${siteName} olarak paydaşlarımıza en yüksek katma değeri sunmak ve sektör standartlarını yukarı taşımak için çalışıyoruz. Şeffaflık, dürüstlük ve sürdürülebilirlik temel ilkelerimizdir.`,
              imageUrl:
                'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1200&auto=format&fit=crop',
              buttonText: 'İletişime Geçin',
              buttonLink: '/iletisim',
            },
            style: { backgroundColor: '#FFFFFF', textColor: '#0F172A' },
            createdAt: now,
            updatedAt: now,
          },
          {
            id: this.generateUniqueId('sec_team'),
            siteId,
            type: 'TEAM',
            name: 'Yönetim ve Kadro',
            isVisible: true,
            order: 3,
            content: {
              badge: 'EKİBİMİZ',
              title: 'Deneyimli ve Vizyoner Kadro',
              description:
                'Farklı disiplinlerde uzmanlaşmış dinamik profesyonellerimizle hedeflerinize emin adımlarla ilerleyin.',
              items: [],
            },
            style: { backgroundColor: '#F8FAFC', textColor: '#0F172A' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, false),
        ],
      });

      // Page 3: Hizmetlerimiz
      pages.push({
        id: this.generateUniqueId('page_services'),
        siteId,
        title: 'Hizmetlerimiz',
        slug: '/hizmetler',
        seoTitle: `Hizmetlerimiz — ${siteName}`,
        seoDescription: `${siteName} kurumsal hizmet kataloğu ve profesyonel çözümler.`,
        isHomePage: false,
        isPublished: true,
        order: 2,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Teklif Alın', '/iletisim', false),
          this.createPageHeroSection(
            siteId,
            'HİZMETLERİMİZ',
            'Kurumunuza Değer Katan Çözümler',
            'İşletmenizin ihtiyaçlarına göre uyarlanmış stratejik kurumsal hizmetler.',
            'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop',
            false
          ),
          {
            id: this.generateUniqueId('sec_services_catalog'),
            siteId,
            type: 'SERVICES',
            name: 'Hizmet Kataloğu',
            isVisible: true,
            order: 2,
            content: {
              badge: 'HİZMET DETAYLARI',
              title: 'Kapsamlı Hizmet Yelpazemiz',
              description:
                'Tüm kurumsal süreçlerinizi kolaylaştıran, verimlilik ve kalite odaklı hizmetlerimiz.',
              items: [],
            },
            style: { backgroundColor: '#F8FAFC', textColor: '#0F172A' },
            createdAt: now,
            updatedAt: now,
          },
          {
            id: this.generateUniqueId('sec_services_cta'),
            siteId,
            type: 'CONTACT',
            name: 'Teklif Formu',
            isVisible: true,
            order: 3,
            content: {
              badge: 'TEKLİF ALIN',
              title: 'Hizmetlerimiz Hakkında Bilgi Alın',
              description: 'İhtiyacınıza en uygun hizmet paketini birlikte belirleyelim.',
              buttonText: 'Teklif İste',
              phone: contactInfo.phone || '',
              email: contactInfo.email || '',
              address: contactInfo.address || '',
            },
            style: { backgroundColor: '#0F172A', textColor: '#FFFFFF' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, false),
        ],
      });

      // Page 4: Projelerimiz
      pages.push({
        id: this.generateUniqueId('page_projects'),
        siteId,
        title: 'Projelerimiz',
        slug: '/projeler',
        seoTitle: `Projelerimiz & Referanslar — ${siteName}`,
        seoDescription: `${siteName} tamamlanan projeler ve kurumsal referanslar.`,
        isHomePage: false,
        isPublished: true,
        order: 3,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Teklif Alın', '/iletisim', false),
          this.createPageHeroSection(
            siteId,
            'PORTFOLYO',
            'Tamamlanan Başarılı Projeler',
            'Sektörün önde gelen kurumları ile hayata geçirdiğimiz değer katan çalışmalar.',
            'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1600&auto=format&fit=crop',
            false
          ),
          {
            id: this.generateUniqueId('sec_references_grid'),
            siteId,
            type: 'REFERENCES',
            name: 'Referanslar ve Ortaklıklar',
            isVisible: true,
            order: 2,
            content: {
              badge: 'REFERANSLARIMIZ',
              title: 'Güvenilir Çözüm Ortaklıklarımız',
              description: 'Başarıyla sonuçlandırdığımız projeler ve mutlu kurumsal iş birlikleri.',
              items: [],
            },
            style: { backgroundColor: '#FFFFFF', textColor: '#0F172A' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, false),
        ],
      });

      // Page 5: Galeri
      pages.push({
        id: this.generateUniqueId('page_gallery'),
        siteId,
        title: 'Galeri',
        slug: '/galeri',
        seoTitle: `Kurumsal Galeri — ${siteName}`,
        seoDescription: `${siteName} kurumsal yaşam ve etkinlik fotoğraf galerisi.`,
        isHomePage: false,
        isPublished: true,
        order: 4,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Teklif Alın', '/iletisim', false),
          this.createPageHeroSection(
            siteId,
            'FOTOĞRAFLAR',
            'Kurumsal Fotoğraf Galerisi',
            'Çalışma ortamımız, kurumsal etkinliklerimiz ve projelerimizden kareler.',
            'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1600&auto=format&fit=crop',
            false
          ),
          {
            id: this.generateUniqueId('sec_gallery_media'),
            siteId,
            type: 'GALLERY',
            name: 'Fotoğraf Galerisi',
            isVisible: true,
            order: 2,
            content: {
              badge: 'MEDYA ARŞİVİ',
              title: 'Faaliyetlerimizden Görseller',
              description: 'Kurumumuzun çalışmalarından seçkin anlar.',
              items: [],
            },
            style: { backgroundColor: '#F8FAFC', textColor: '#0F172A' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, false),
        ],
      });

      // Page 6: İletişim
      pages.push({
        id: this.generateUniqueId('page_contact'),
        siteId,
        title: 'İletişim',
        slug: '/iletisim',
        seoTitle: `İletişim — ${siteName}`,
        seoDescription: `${siteName} kurumsal iletişim bilgileri, form ve harita.`,
        isHomePage: false,
        isPublished: true,
        order: 5,
        createdAt: now,
        updatedAt: now,
        sections: [
          this.createHeaderSection(siteId, siteName, 'Teklif Alın', '/iletisim', false),
          this.createPageHeroSection(
            siteId,
            'İLETİŞİM',
            'Bizimle İletişime Geçin',
            'Projeleriniz ve kurumsal sorularınız için uzman ekibimizle dilediğiniz zaman görüşebilirsiniz.',
            'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1600&auto=format&fit=crop',
            false
          ),
          {
            id: this.generateUniqueId('sec_contact_form'),
            siteId,
            type: 'CONTACT',
            name: 'İletişim Formu & Bilgiler',
            isVisible: true,
            order: 2,
            content: {
              badge: 'BİZE YAZIN',
              title: 'Mesajınızı Bırakın, Hemen Dönelim',
              description:
                'Görüş, öneri ve teklif taleplerinizi aşağıdaki formdan iletebilirsiniz.',
              buttonText: 'Mesajı Gönder',
              phone: contactInfo.phone || '',
              email: contactInfo.email || '',
              address: contactInfo.address || '',
            },
            style: { backgroundColor: '#FFFFFF', textColor: '#0F172A' },
            createdAt: now,
            updatedAt: now,
          },
          this.createFooterSection(siteId, siteName, contactInfo, false),
        ],
      });
    }

    const defaultSettings: Partial<SiteSettings> = {
      siteId,
      siteName,
      slug,
      industry,
      logoText: siteName,
      primaryColor,
      secondaryColor,
      accentColor,
      fontFamily,
      headingFont,
      phone: contactInfo.phone || '',
      email: contactInfo.email || '',
      address: contactInfo.address || '',
      headerMenu,
      headerButtonText: isYacht ? 'Rezervasyon Yap' : 'Teklif Al',
      headerButtonAction: {
        type: 'page',
        targetPage: isYacht ? '/rezervasyon' : '/iletisim',
      },
      socialLinks: {
        whatsapp: contactInfo.whatsapp || (contactInfo.phone ? `https://wa.me/${contactInfo.phone.replace(/[^0-9]/g, '')}` : ''),
      },
      seo: {
        title: `${siteName} — ${isYacht ? 'Lüks Yat Kiralama & Mavi Tur' : 'Resmi Kurumsal Web Sitesi'}`,
        description: `${siteName} resmi web sitesi. ${isYacht ? 'Lüks mavi tur ve yat kiralama.' : 'Kurumsal çözümler ve profesyonel hizmetler.'}`,
        keywords: isYacht
          ? ['yat kiralama', 'mavi tur', 'gulet', 'katamaran', (siteName || '').toLowerCase()].filter(Boolean)
          : ['kurumsal', 'danışmanlık', 'hizmetler', (siteName || '').toLowerCase()].filter(Boolean),
      },
      servicesList: [],
      testimonialsList: [],
      referencesList: [],
      updatedAt: now,
    };

    return {
      pages,
      headerMenu,
      defaultSettings,
    };
  }

  private static createHeaderSection(
    siteId: string,
    siteName: string,
    buttonText: string,
    buttonLink: string,
    isDark: boolean
  ): PageSection {
    const now = Date.now();
    return {
      id: this.generateUniqueId('sec_header'),
      siteId,
      type: 'HEADER',
      name: 'Üst Navigasyon Menüsü',
      isVisible: true,
      order: 0,
      content: {
        title: siteName,
        subtitle: '',
        buttonText,
        buttonLink,
      },
      style: {
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.98)',
        textColor: isDark ? '#FFFFFF' : '#0F172A',
      },
      createdAt: now,
      updatedAt: now,
    };
  }

  private static createPageHeroSection(
    siteId: string,
    badge: string,
    title: string,
    description: string,
    bgImageUrl: string,
    isDark: boolean
  ): PageSection {
    const now = Date.now();
    return {
      id: this.generateUniqueId('sec_hero_page'),
      siteId,
      type: 'HERO',
      name: 'Sayfa Başlık Alanı',
      isVisible: true,
      order: 1,
      content: {
        badge,
        title,
        description,
        bgImageUrl,
        bgOverlayOpacity: isDark ? 75 : 65,
      },
      style: {
        textColor: '#FFFFFF',
        paddingTop: '80px',
        paddingBottom: '80px',
      },
      createdAt: now,
      updatedAt: now,
    };
  }

  private static createFooterSection(
    siteId: string,
    siteName: string,
    contactInfo: { phone?: string; email?: string; address?: string },
    isDark: boolean
  ): PageSection {
    const now = Date.now();
    return {
      id: this.generateUniqueId('sec_footer'),
      siteId,
      type: 'FOOTER',
      name: 'Alt Bilgi (Footer)',
      isVisible: true,
      order: 99,
      content: {
        title: siteName,
        description: `${siteName} resmi web platformudur. Tüm hakları saklıdır.`,
        phone: contactInfo.phone || '',
        email: contactInfo.email || '',
        address: contactInfo.address || '',
      },
      style: {
        backgroundColor: isDark ? '#020617' : '#0F172A',
        textColor: '#94A3B8',
      },
      createdAt: now,
      updatedAt: now,
    };
  }
}
